import type { CSSProperties } from 'react';
import type { Link, PageSettings, ButtonShape, ButtonBorderStyle, ButtonFill, ButtonShadow } from '@/types/editor';
import { BUTTON_STYLES } from '@/types/editor';

export type LinkGradientType = 'solid' | 'linear' | 'radial' | 'conic';

/**
 * Builds a CSS `background` value from gradient parameters.
 * Safe to use directly in a `style` prop alongside `background:`.
 */
export function computeLinkBackground(
  gradientType: LinkGradientType | null | undefined,
  color1: string,
  color2: string | null | undefined,
  angle: number | null | undefined,
): string {
  const type = gradientType ?? 'solid';
  const c2 = color2 || color1;
  const deg = angle ?? 135;

  switch (type) {
    case 'linear':
      return `linear-gradient(${deg}deg, ${color1}, ${c2})`;
    case 'radial': {
      // angle field stores the X-position percentage (0-100) for radial
      const xPct = Math.max(0, Math.min(100, deg));
      return `radial-gradient(circle at ${xPct}% 50%, ${color1}, ${c2})`;
    }
    case 'conic':
      return `conic-gradient(from ${deg}deg at 50% 50%, ${color1}, ${c2}, ${color1})`;
    default:
      return color1;
  }
}

// ── Button dimension helpers ──────────────────────────────────────────────────

type ButtonDimensions = {
  shape: ButtonShape;
  borderStyle: ButtonBorderStyle;
  fill: ButtonFill;
  shadow: ButtonShadow;
};

/**
 * Extracts the 4 button dimensions from settings.
 * Uses new dimension fields when present; falls back to deriving from legacy `button_style`.
 */
function resolveDimensions(settings: Pick<PageSettings, 'button_style' | 'button_shape' | 'button_border_style' | 'button_fill' | 'button_shadow'>): ButtonDimensions {
  const hasNew = settings.button_shape != null;

  if (hasNew) {
    return {
      shape:       settings.button_shape        ?? 'rounded',
      borderStyle: settings.button_border_style ?? 'none',
      fill:        settings.button_fill         ?? 'filled',
      shadow:      settings.button_shadow       ?? 'none',
    };
  }

  // Legacy fallback — derive from old button_style string
  const bStyle = (settings.button_style as string) || 'rounded';

  const shape: ButtonShape =
    bStyle.endsWith('-pill')   ? 'pill'   :
    bStyle.endsWith('-square') ? 'square' : 'rounded';

  const borderStyle: ButtonBorderStyle =
    bStyle.startsWith('dashed')       ? 'dashed'  :
    bStyle.startsWith('dotted')       ? 'dotted'  :
    bStyle.startsWith('sketch')       ? 'sketch'  :
    bStyle.startsWith('chalk')        ? 'chalk'   :
    bStyle.startsWith('outline')      ? 'solid'   :
    bStyle.startsWith('filled-border')? 'solid'   :
    bStyle.startsWith('shadow')       ? 'solid'   : 'none';

  const fill: ButtonFill =
    ['outline','outline-pill','outline-square',
     'dashed','dashed-pill','dashed-square',
     'dotted','dotted-pill','dotted-square',
     'sketch','chalk'].includes(bStyle) ? 'outline' : 'filled';

  const shadow: ButtonShadow =
    ['shadow','shadow-pill','shadow-square'].includes(bStyle) ? 'flat' : 'none';

  return { shape, borderStyle, fill, shadow };
}

// ── CSS class builder ─────────────────────────────────────────────────────────

/**
 * Returns the Tailwind/custom CSS class string for a button based on
 * the 4 dimension fields (with backward-compat fallback to `button_style`).
 */
export function getButtonClass(
  settings: Pick<PageSettings, 'button_style' | 'button_shape' | 'button_border_style' | 'button_fill' | 'button_shadow'>,
): string {
  // Pure legacy path — if NO new fields exist and button_style does, delegate to BUTTON_STYLES lookup
  if (settings.button_shape == null && settings.button_style != null) {
    return BUTTON_STYLES.find(s => s.value === settings.button_style)?.class ?? 'rounded-xl';
  }

  const { shape, borderStyle, shadow } = resolveDimensions(settings);

  // Organic shapes: combine with the shape dimension
  if (borderStyle === 'sketch') {
    if (shape === 'pill')   return 'btn-border-sketch-pill';
    if (shape === 'square') return 'btn-border-sketch-square';
    return 'btn-border-sketch';
  }
  if (borderStyle === 'chalk') {
    if (shape === 'pill')   return 'btn-border-chalk-pill';
    if (shape === 'square') return 'btn-border-chalk-square';
    return 'btn-border-chalk';
  }

  const shapeClass: Record<ButtonShape, string> = {
    rounded: 'rounded-xl',
    pill:    'rounded-full',
    square:  'rounded-none',
  };

  const borderClass =
    borderStyle === 'solid'  ? 'border-2' :
    borderStyle === 'dashed' ? 'border-2 border-dashed' :
    borderStyle === 'dotted' ? 'border-[3px] border-dotted' : '';

  // Flat shadow needs at least a thin border for the offset effect to be visible
  const shadowBorder = shadow === 'flat' && !borderClass ? 'border-2' : '';

  return [shapeClass[shape], borderClass, shadowBorder].filter(Boolean).join(' ');
}

// ── Inline style helpers ──────────────────────────────────────────────────────

type StyleSettings = Pick<PageSettings,
  | 'button_color' | 'button_text_color' | 'text_color' | 'background_color'
  | 'button_use_theme_colors'
  | 'button_style' | 'button_shape' | 'button_border_style' | 'button_fill' | 'button_shadow'
  | 'button_shadow_color' | 'button_shadow_x' | 'button_shadow_y' | 'button_shadow_blur' | 'button_shadow_spread'
  | 'button_border_color'
>;

/**
 * Returns the effective button background and text colors.
 * When `button_use_theme_colors` is true, derives them from the active page
 * theme (text_color as button bg, background_color as button text) so buttons
 * always look native to the theme without manual color picks.
 * Falls back to safe defaults when explicit color fields are empty/null.
 */
export function resolveButtonColors(settings: StyleSettings): { btnBg: string; btnText: string } {
  if (settings.button_use_theme_colors) {
    return {
      btnBg:   settings.text_color       || '#1a1a2e',
      btnText: settings.background_color || '#ffffff',
    };
  }
  return {
    btnBg:   settings.button_color      || settings.text_color       || '#1a1a2e',
    btnText: settings.button_text_color || settings.background_color || '#ffffff',
  };
}

function buildButtonStyle(
  background: string,
  textColor: string,
  dims: ButtonDimensions,
  settings: StyleSettings,
): CSSProperties {
  const { borderStyle, fill, shadow } = dims;

  const hasBorder = borderStyle !== 'none';
  const isTransparent = fill === 'outline';

  const color = isTransparent ? settings.text_color : textColor;
  const bg    = isTransparent ? 'transparent' : background;

  const style: CSSProperties = { background: bg, color };

  if (hasBorder || shadow === 'flat' || shadow === 'soft') {
    style.borderColor = settings.button_border_color || color;
  } else {
    style.border = 'none';
  }

  if (shadow === 'flat' || shadow === 'soft') {
    const x      = settings.button_shadow_x      ?? 4;
    const y      = settings.button_shadow_y      ?? 4;
    const spread = settings.button_shadow_spread ?? 0;
    const blur   = shadow === 'soft' ? (settings.button_shadow_blur  ?? 8) : 0;
    const col    = settings.button_shadow_color  || settings.text_color || '#000000';
    style.boxShadow = `${x}px ${y}px ${blur}px ${spread}px ${col}`;
  }

  return style;
}

/**
 * Inline style for global buttons (store, contact) derived purely from page settings.
 */
export function getGlobalButtonStyle(settings: StyleSettings): CSSProperties {
  const dims = resolveDimensions(settings);
  const { btnBg, btnText } = resolveButtonColors(settings);
  return buildButtonStyle(btnBg, btnText, dims, settings);
}

/**
 * Inline style for per-link buttons, respecting per-link color overrides and gradients.
 */
export function getLinkButtonStyle(
  link: Pick<
    Link,
    | 'button_color'
    | 'button_text_color'
    | 'button_gradient_type'
    | 'button_gradient_end'
    | 'button_gradient_angle'
    | 'use_global_theme'
  >,
  settings: StyleSettings,
): CSSProperties {
  const dims = resolveDimensions(settings);
  const { btnBg, btnText } = resolveButtonColors(settings);

  // Per-link colors override only if NOT using theme colors and link has custom values
  const useGlobal = !!link.use_global_theme || !!settings.button_use_theme_colors;
  const background =
    useGlobal || !link.button_color
      ? btnBg
      : computeLinkBackground(
          link.button_gradient_type,
          link.button_color,
          link.button_gradient_end,
          link.button_gradient_angle,
        );
  const color =
    useGlobal || !link.button_text_color
      ? btnText
      : link.button_text_color;

  return buildButtonStyle(background, color, dims, settings);
}
